/**
 * ModularPatientDataContext.js
 *
 * Provider-side data context for patients using the subcollection-based data
 * architecture (Adult Autism, future CYP modules). This replaces PatientDataContext
 * for any patient whose assessmentModule is NOT 'adultAdhd'.
 *
 * Key architectural differences from PatientDataContext:
 *
 * 1. READS: Instead of a single monolithic getDoc on patients/{uid}, this context
 *    reads the slim header document PLUS getDocs on the pathway form subcollection
 *    (adult_autism/assessment_001/formInstances), assessment stages
 *    (adult_autism/assessment_001/assessmentStages), and report content
 *    (adult_autism/assessment_001/reportContent) — four parallel reads.
 *
 * 2. STATE SHAPE: Exposes { header, formInstances, assessmentStages, reportContent }
 *    rather than a flat patientDataModel. Consumers access form data via
 *    formInstances[formKey] and report data via reportContent[stageKey].
 *
 * 3. WRITES: Individual assessment stage documents are written via updateAssessmentStage,
 *    which queues dot-path updates into a per-stage 200ms debounced buffer.
 *    When the debounce timer fires, all accumulated updates are merged into a
 *    single Firestore updateDoc call — reducing write volume, billing, and
 *    context-state patch races compared to immediate fire-and-forget writes.
 *    The on-screen stage updates immediately, the same way an Adult ADHD
 *    proforma updates its own button state before the save returns. The
 *    Firestore write still waits for the 200ms debounce and is confirmed
 *    afterwards. A slow save must not paint an older answer back over a
 *    newer click. The debounce interval matches PatientDataContext.
 *
 * 4. CONCURRENCY: Per-document writes eliminate the monolithic-doc contention that
 *    the ADHD PatientDataContext works around with runTransaction. Writes to
 *    different dot paths on the same document are field-level atomic in Firestore,
 *    so no transactions are needed. A per-stage mutex prevents concurrent
 *    updateDoc calls to the same stage document; updates arriving during an
 *    in-flight write accumulate in the buffer and are drained on completion.
 *
 * 5. MODULE-AGNOSTIC: This context works for ANY subcollection-based module. The
 *    assessmentModule field on the header determines which UI to render, but the
 *    data access pattern is identical across modules.
 *
 * The context uses the same access control pattern as PatientDataContext:
 * - Clinician doc is cached for 5 minutes (session cache)
 * - Access check: Org Admin/Support by org match, Clinicians by assignment
 * - Retry-once safeguard for stale clinician cache
 *
 * localStorage key: 'td_modular_patient_data' (distinct from ADHD's 'patientDataModel')
 *
 * Cold-load behaviour: state AND the write target (patient id + module) are
 * seeded from that cache, so the page paints instantly and a click made before
 * fetchPatientData returns is saved, as it is in the ADHD context. The fetch
 * then lays any still-outstanding batches over the server snapshot so it
 * cannot paint a pre-click value. See readCachedWriteTarget and the overlay
 * block in fetchPatientData.
 */
import React, { createContext, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, getDocs, updateDoc, serverTimestamp, FieldValue } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  buildAutismSubcollectionQuery,
  hasOrgWideAutismSubcollectionAccess,
} from '../utils/autismFirestoreQueries';
import {
  getAssessmentStagePathSegments,
  getReportContentPathSegments,
} from '../constants/modularFirestorePaths';
import {
  getModuleCollection,
  getModuleAssessmentDocId,
  moduleHasPatientFormInstances,
  moduleHasInformantHistoryStage,
} from '../constants/assessmentModules';
import isPlainObject from 'lodash/isPlainObject';
import { asdLog } from '../utils/autismDebugLog';

export const ModularPatientDataContext = createContext();

/**
 * Detects whether a value is a Firestore serverTimestamp() sentinel.
 *
 * Uses the public API only: `instanceof FieldValue` (exported by
 * firebase/firestore) plus `isEqual(serverTimestamp())` to distinguish
 * server-timestamp sentinels from other FieldValue types like
 * deleteField() or arrayUnion(). No reliance on internal properties
 * like `_methodName` or `.type`.
 *
 * Used by `applyDotPathUpdates` to approximate the server value locally
 * as an ISO date string for immediate display; the real Firestore
 * Timestamp replaces it on the next read.
 */
function isServerTimestampSentinel(value) {
  // Lazy: create the reference sentinel on first call rather than at module
  // level so test files that mock firebase/firestore without providing
  // serverTimestamp do not crash on import.
  return value instanceof FieldValue && value.isEqual(serverTimestamp());
}

const CLINICIAN_DOC_TTL_MS = 5 * 60 * 1000;
const LOCALSTORAGE_KEY = 'td_modular_patient_data';
const LOADED_PATIENT_ID_KEY = 'td_modular_loaded_patient_id';

/**
 * Read, from the localStorage cache, the patient and module that writes
 * should target before fetchPatientData has run.
 *
 * Why this exists: on a cold load (hard refresh, or opening the app on a
 * patient page) the provider paints instantly from the cache, and the route
 * wrapper lets the page go interactive as soon as the cached loadedPatientId
 * matches the URL. Adult ADHD accepts clicks in that window because its
 * writes take the patient id from the caller. The modular writes resolve
 * their target from two refs instead, and until this helper existed those
 * refs were null until the fetch returned, so a click in the first second
 * or two after a refresh was dropped without a toast and the page snapped
 * back when the fetch landed. Seeding the refs from the same cache the
 * state hydrates from closes that gap.
 *
 * The two values always describe the same patient: fetchPatientData writes
 * the header and the id in the same tick, and clearPatientData removes both.
 * The route wrapper only renders children when the cached id matches the
 * URL, so a seeded target can never be used for a different patient's page.
 *
 * Returns nulls when there is no cache (first ever load, or after sign-out),
 * in which case writes stay disabled until the fetch returns, as before.
 */
function readCachedWriteTarget() {
  try {
    const patientId = localStorage.getItem(LOADED_PATIENT_ID_KEY) || null;
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    const header = stored ? JSON.parse(stored).header : null;
    const module = header?.assessmentModule || null;
    return { patientId, module };
  } catch {
    return { patientId: null, module: null };
  }
}

/**
 * Recursively strips `undefined` values from an object or array tree.
 * Firestore's updateDoc rejects any field whose value is `undefined`,
 * but upstream components sometimes set optional fields to `undefined`
 * (e.g. StructuredNotesEditor's `questions` / `domains` on note objects).
 * This sanitiser removes those entries before the write reaches Firestore.
 *
 * IMPORTANT: only recurses into plain objects and arrays. Class instances
 * (FieldValue sentinels, Firestore Timestamps, Dates, DocumentReferences)
 * are returned as-is. The Firestore SDK recognises sentinels via
 * `instanceof FieldValue`; rebuilding them with Object.entries strips the
 * prototype and produces an ordinary map `{ _methodName: 'serverTimestamp' }`
 * that Firestore stores literally rather than interpreting as a server
 * timestamp. lodash `isPlainObject` is the same predicate the ADHD
 * PatientDataContext's `deepMerge` relies on (lodash `mergeWith` only
 * recurses into plain objects), so the two contexts now share one
 * serialisation semantic.
 */
function stripUndefined(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map(item => stripUndefined(item)).filter(item => item !== undefined);
  }
  // Class instances (FieldValue, Timestamp, Date, etc.) must pass through
  // untouched so the Firestore SDK can recognise them by prototype.
  if (!isPlainObject(value)) return value;
  const result = {};
  for (const [k, v] of Object.entries(value)) {
    const cleaned = stripUndefined(v);
    if (cleaned !== undefined) result[k] = cleaned;
  }
  return result;
}

export const ModularPatientDataProvider = ({ children }) => {

  // State: split into header, formInstances, and assessmentStages rather than
  // one flat document. Hydrated from localStorage on cold load for instant paint.
  const [header, setHeader] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      return stored ? JSON.parse(stored).header || null : null;
    } catch { return null; }
  });

  const [formInstances, setFormInstances] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      return stored ? JSON.parse(stored).formInstances || {} : {};
    } catch { return {}; }
  });

  const [assessmentStages, setAssessmentStages] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      return stored ? JSON.parse(stored).assessmentStages || {} : {};
    } catch { return {}; }
  });

  // Report content documents (keyed by stageKey, e.g. 'history', 'semiStructuredInterview').
  // Stores LLM-generated paragraph outputs, structured inputs, approval state, and
  // edit history for each assessment stage. Loaded in parallel with assessmentStages
  // from the reportContent subcollection under the assessment instance document.
  const [reportContent, setReportContent] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      return stored ? JSON.parse(stored).reportContent || {} : {};
    } catch { return {}; }
  });

  // Informant history stage documents (keyed by informant UID).
  // Fetched alongside patient data when informants exist on the patient.
  const [informantHistoryStages, setInformantHistoryStages] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      return stored ? JSON.parse(stored).informantHistoryStages || {} : {};
    } catch { return {}; }
  });

  // Informant form instances (keyed by informant UID) — used by the History
  // module to resolve informant prefill when history-stage fields are empty.
  const [informantFormInstances, setInformantFormInstances] = useState(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      return stored ? JSON.parse(stored).informantFormInstances || {} : {};
    } catch { return {}; }
  });

  // Render-gating: same pattern as PatientDataContext.loadedPatientId.
  // Children only render when this matches the URL patientId.
  const [loadedPatientId, setLoadedPatientId] = useState(() => {
    return localStorage.getItem(LOADED_PATIENT_ID_KEY) || null;
  });

  // Write target: which patient, and which module's subcollection, the
  // write/refresh callbacks address. Seeded from the cache so a click in the
  // cold-load window (page already interactive, fetch not yet back) is saved
  // rather than dropped — see readCachedWriteTarget. fetchPatientData
  // overwrites both from the fetched header; clearPatientData nulls both.
  // Read once: useRef evaluates its argument on every render, so the cache
  // parse is done in a lazy useState initialiser instead.
  const [cachedWriteTarget] = useState(readCachedWriteTarget);
  const loadedPatientIdRef = useRef(cachedWriteTarget.patientId);

  // The assessmentModule for the loaded patient, used by getStageSegments /
  // getReportSegments to build the right subcollection path for any modular
  // module without re-reading the header.
  const loadedModuleRef = useRef(cachedWriteTarget.module);

  // Set ONLY by a successful fetchPatientData, never from the cache. Drives
  // one decision: when a fetch fails, "is this a re-fetch of a patient we
  // have already confirmed?" (preserve what is on screen) versus "we have
  // nothing confirmed for this patient" (clear and surface the error). The
  // cache-seeded ref above must not be used for that, or a cold load that
  // hits a network error would silently keep showing cached data where it
  // used to clear — this keeps the error path exactly as it was, and
  // identical to PatientDataContext's.
  const fetchConfirmedPatientIdRef = useRef(null);

  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Clinician doc session cache (identical to PatientDataContext)
  const clinicianDocCache = useRef({ data: null, uid: null, fetchedAt: 0 });

  const getUserClinicianDoc = useCallback(async ({ forceRefresh = false } = {}) => {
    const user = auth.currentUser;
    if (!user) return null;

    const cache = clinicianDocCache.current;
    if (
      !forceRefresh &&
      cache.data &&
      cache.uid === user.uid &&
      (Date.now() - cache.fetchedAt) < CLINICIAN_DOC_TTL_MS
    ) {
      return cache.data;
    }

    const clinicianDocRef = doc(db, 'clinicians', user.uid);
    const clinicianDoc = await getDoc(clinicianDocRef);
    if (!clinicianDoc.exists()) return null;

    const data = clinicianDoc.data();
    clinicianDocCache.current = { data, uid: user.uid, fetchedAt: Date.now() };
    return data;
  }, []);

  const checkClinicianAccess = (clinicianData, patientId, patientData) => {
    if (clinicianData.userType === 'Org. Admin' || clinicianData.userType === 'Support') {
      return patientData && patientData.organisation === clinicianData.organisation;
    }
    return !!(clinicianData.patients && clinicianData.patients[patientId]);
  };

  // ─── Write infrastructure ──────────────────────────────────────────
  // ─── Module-resolved path helpers ────────────────────────────────
  // These closures read from loadedModuleRef.current (set once during patient
  // fetch) so write/refresh callbacks always target the correct subcollection
  // regardless of which modular module the patient belongs to.
  const getStageSegments = useCallback((patientId, stageKey) => {
    const mod = loadedModuleRef.current || 'adultAutism';
    const col = getModuleCollection(mod) || 'adult_autism';
    const docId = getModuleAssessmentDocId(mod) || 'assessment_001';
    return getAssessmentStagePathSegments(patientId, col, docId, stageKey);
  }, []);

  const getReportSegments = useCallback((patientId, stageKey) => {
    const mod = loadedModuleRef.current || 'adultAutism';
    const col = getModuleCollection(mod) || 'adult_autism';
    const docId = getModuleAssessmentDocId(mod) || 'assessment_001';
    return getReportContentPathSegments(patientId, col, docId, stageKey);
  }, []);

  // In-flight write promises keyed by stageKey. Each stage has a Set of
  // Promises representing active updateDoc calls. flushStageWrites awaits
  // all promises in the relevant Set(s).
  const inFlightWrites = useRef(new Map());
  // Callbacks waiting on flushStageWrites to resolve.
  const flushCallbacks = useRef([]);
  const MAX_WRITE_RETRIES = 3;
  const WRITE_ERROR_TOAST_ID = 'modular-stage-save-error';
  const DEBOUNCE_MS = 200;

  // Per-stage pending dot-path updates awaiting the debounce timer. Each key
  // is a stageKey, each value is a flat object of dot-path -> value entries
  // accumulated from successive updateAssessmentStage calls.
  const pendingUpdates = useRef(new Map());

  // Per-stage debounce timer IDs (setTimeout handles). Cleared on flush,
  // patient switch, and sign-out to prevent stale writes.
  const debounceTimers = useRef(new Map());

  // Per-stage mutex: prevents concurrent updateDoc calls to the same stage
  // document. When a write is in flight and new updates arrive, they
  // accumulate in pendingUpdates and are drained once the write completes.
  const processingStage = useRef(new Set());

  // The CONTENT of each batch currently in flight (queue key -> dot-path
  // updates), alongside inFlightWrites which only holds the promises.
  // fetchPatientData reads this so a fetch that lands while a write is
  // still travelling can lay the write back over the server snapshot
  // instead of painting the pre-click value for a round-trip. Set just
  // before updateDoc, removed in the write's finally block.
  const inFlightBatches = useRef(new Map());

  const clearPatientData = useCallback(() => {
    setHeader(null);
    setFormInstances({});
    setAssessmentStages({});
    setReportContent({});
    setInformantHistoryStages({});
    setInformantFormInstances({});
    loadedPatientIdRef.current = null;
    loadedModuleRef.current = null;
    fetchConfirmedPatientIdRef.current = null;
    setLoadedPatientId(null);
    localStorage.removeItem(LOCALSTORAGE_KEY);
    localStorage.removeItem(LOADED_PATIENT_ID_KEY);
    clinicianDocCache.current = { data: null, uid: null, fetchedAt: 0 };
    setError(null);
    // Cancel all debounce timers to prevent stale writes from firing into
    // a different patient's document after patient switch or sign-out.
    debounceTimers.current.forEach(t => clearTimeout(t));
    debounceTimers.current.clear();
    pendingUpdates.current.clear();
    inFlightBatches.current.clear();
    processingStage.current.clear();

    // Discard in-flight write tracking and resolve any pending flush callbacks
    // to prevent cross-patient contamination and hanging promises on sign-out.
    inFlightWrites.current = new Map();
    const pending = flushCallbacks.current.splice(0);
    pending.forEach(cb => cb.resolve());
  }, []);

  // ─── Write API ─────────────────────────────────────────────────────

  /**
   * Patch the localStorage cache for a single assessmentStage without
   * re-serialising the entire state tree. Mirrors refreshFormInstance's
   * approach of reading → patching → writing the LOCALSTORAGE_KEY blob.
   */
  const patchAssessmentStageLocalStorage = useCallback((stageKey, stageData) => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.assessmentStages = { ...parsed.assessmentStages, [stageKey]: stageData };
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(parsed));
      }
    } catch { /* non-critical — data is in Firestore */ }
  }, []);

  /**
   * Patch the localStorage cache for a single reportContent doc without
   * re-serialising the entire state tree. Same approach as
   * patchAssessmentStageLocalStorage.
   */
  const patchReportContentLocalStorage = useCallback((stageKey, stageData) => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.reportContent = { ...parsed.reportContent, [stageKey]: stageData };
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(parsed));
      }
    } catch { /* non-critical — data is in Firestore */ }
  }, []);

  /**
   * Apply dot-path updates to a nested object without mutating the original.
   * Used to merge confirmed writes into the in-memory assessmentStages state.
   *
   * Example: applyDotPathUpdates({ data: { criteria: {} } }, { 'data.criteria.A1.notes': [] })
   * => { data: { criteria: { A1: { notes: [] } } } }
   *
   * serverTimestamp() sentinels are approximated as ISO date strings for
   * immediate display. The real Firestore Timestamp replaces them on the
   * next fetchPatientData read. Detection uses the public FieldValue API
   * (see isServerTimestampSentinel) — no reliance on internal SDK fields.
   *
   * Other FieldValue types (deleteField, arrayUnion, increment) are never
   * passed through the context queue — they are used only via direct
   * updateDoc calls in the Review Document pages — so they are left as-is
   * here. If that ever changes, this function will need corresponding
   * local approximations.
   */
  const applyDotPathUpdates = useCallback((obj, updates) => {
    const result = JSON.parse(JSON.stringify(obj || {}));
    for (const [dotPath, value] of Object.entries(updates)) {
      const parts = dotPath.split('.');
      let cursor = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (cursor[parts[i]] == null || typeof cursor[parts[i]] !== 'object') {
          cursor[parts[i]] = {};
        }
        cursor = cursor[parts[i]];
      }
      // serverTimestamp() produces a FieldValue sentinel that cannot be
      // resolved client-side. Approximate it as an ISO string so date
      // display helpers (toLondonCalendarDateString, etc.) can parse it
      // immediately. The real server timestamp lands on the next read.
      if (isServerTimestampSentinel(value)) {
        cursor[parts[parts.length - 1]] = new Date().toISOString();
      } else {
        cursor[parts[parts.length - 1]] = value;
      }
    }
    return result;
  }, []);

  /**
   * Internal: process the pending update queue for a single assessment stage.
   * Snapshots all accumulated dot-path updates, merges them into one Firestore
   * updateDoc call, and patches context state + localStorage on success.
   *
   * Per-stage mutex (processingStage) prevents concurrent writes to the same
   * document. If new updates arrive while a write is in flight, they accumulate
   * in pendingUpdates and drain automatically when the current write completes
   * (deferred-drain pattern, analogous to PatientDataContext's isProcessing /
   * pendingDrainPatientId mechanism).
   */
  const processQueue = useCallback(async (stageKey) => {
    // Mutex: if a write to this stage is already in flight, the updates
    // stay in pendingUpdates and will be drained in the finally block.
    if (processingStage.current.has(stageKey)) return;

    // Snapshot and clear the pending buffer for this stage
    const updates = pendingUpdates.current.get(stageKey);
    pendingUpdates.current.delete(stageKey);
    if (!updates || Object.keys(updates).length === 0) {
      if (pendingUpdates.current.size === 0 && inFlightWrites.current.size === 0) {
        const cbs = flushCallbacks.current.splice(0);
        cbs.forEach(cb => cb.resolve());
      }
      return;
    }

    const patientId = loadedPatientIdRef.current;
    if (!patientId) return;
    if (!auth.currentUser) return;

    processingStage.current.add(stageKey);

    // Patch the localStorage cache with this batch BEFORE the Firestore
    // round-trip, not only after it is acknowledged (the post-ack patch below
    // still runs and is the one that normally lands).
    //
    // Why: this provider is NOT remounted when the clinician moves between
    // two modular routes (React Router renders sibling routes without a key,
    // and every modular route shares the same ProtectedRoute > Provider >
    // RouteWrapper prefix, so React updates the live instance). In-memory
    // state therefore already carries the optimistic value across CI → DD →
    // PAD. The provider IS torn down when the clinician leaves the modular
    // area entirely (dashboard, the legacy /patient/:id overview), and the
    // route-change useLayoutEffect flushes the queue as it goes — but the
    // flush is fire-and-forget, so the write is acknowledged after unmount,
    // React drops the post-ack setState (and with it the post-ack localStorage
    // patch), and the next provider to mount hydrates from a cache that never
    // learned about the last batch. It shows the stale value until its own
    // fetchPatientData returns.
    //
    // Patching here closes that window: flushStageWrites kicks processQueue
    // synchronously inside the layout-effect cleanup, so the cache is current
    // before any new provider can read it. Once per batch keeps the cost to
    // one JSON parse/stringify per 200 ms rather than one per keystroke.
    //
    // Trade-off: if the write later fails, localStorage holds a value Firestore
    // rejected until the next fetchPatientData replaces it. In-memory state
    // already accepts that trade (it moved on the click), and the error toast
    // below still fires. The cache is only ever a hydration seed; Firestore is
    // the source of truth and every mount re-fetches.
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const stages = parsed.assessmentStages || {};
        stages[stageKey] = applyDotPathUpdates(stages[stageKey] || {}, updates);
        parsed.assessmentStages = stages;
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(parsed));
      }
    } catch { /* non-critical — Firestore is the source of truth */ }

    const stageDocSegments = getStageSegments(patientId, stageKey);
    const stageDocRef = doc(db, ...stageDocSegments);
    const payload = stripUndefined({ ...updates, updatedAt: serverTimestamp() });
    inFlightBatches.current.set(stageKey, updates);

    const writePromise = (async () => {
      let lastError = null;
      for (let attempt = 1; attempt <= MAX_WRITE_RETRIES; attempt++) {
        try {
          if (!auth.currentUser) return;
          await updateDoc(stageDocRef, payload);

          setAssessmentStages(prev => {
            const currentStage = prev[stageKey] || {};
            // A click that happened while this write was in flight is already
            // on screen and sitting in the pending buffer. Re-applying this
            // older payload would put the previous answer back for a moment.
            const newer = pendingUpdates.current.get(stageKey) || {};
            const confirmed = {};
            for (const [key, value] of Object.entries(updates)) {
              if (Object.prototype.hasOwnProperty.call(newer, key)) continue;
              confirmed[key] = value;
            }
            const patched = applyDotPathUpdates(currentStage, confirmed);
            patchAssessmentStageLocalStorage(stageKey, patched);
            return { ...prev, [stageKey]: patched };
          });
          asdLog('ctx', 'write committed', {
            stageKey,
            keys: Object.keys(updates),
            attempt,
          });
          return;
        } catch (err) {
          lastError = err;
          const code = err?.code || '';
          if (['permission-denied', 'not-found', 'unauthenticated'].includes(code)) {
            break;
          }
          if (attempt < MAX_WRITE_RETRIES) {
            await new Promise(r => setTimeout(r, 200 * attempt));
          }
        }
      }

      // All retries exhausted or permanent error — surface diagnostic info
      // and a user-facing toast. Intentionally avoid setError() to prevent
      // blank-page render cascades (same rationale as PatientDataContext).
      const code = lastError?.code || 'unknown';
      if (code === 'not-found') {
        console.error(
          `[ModularPatientDataContext] Stage document does not exist for "${stageKey}"`,
          `— verify patient setup completed. docPath=${stageDocSegments.join('/')}`,
          lastError,
        );
      } else {
        console.error(
          `[ModularPatientDataContext] Write failed for ${stageKey}`,
          `code=${code}`,
          `paths=${Object.keys(updates).join(', ')}`,
          `docPath=${stageDocSegments.join('/')}`,
          lastError,
        );
      }
      const devSuffix = process.env.NODE_ENV !== 'production' ? ` [${code}]` : '';
      toast.error(
        `Your latest changes may not have saved. Please check your connection and try again.${devSuffix}`,
        { toastId: WRITE_ERROR_TOAST_ID },
      );
    })();

    // Track the in-flight promise so flushStageWrites can await it.
    // With the debounced queue each stage has at most ONE active promise
    // at a time (the mutex prevents concurrent writes to the same stage).
    if (!inFlightWrites.current.has(stageKey)) {
      inFlightWrites.current.set(stageKey, new Set());
    }
    inFlightWrites.current.get(stageKey).add(writePromise);

    writePromise.finally(() => {
      const stageSet = inFlightWrites.current.get(stageKey);
      if (stageSet) {
        stageSet.delete(writePromise);
        if (stageSet.size === 0) inFlightWrites.current.delete(stageKey);
      }
      inFlightBatches.current.delete(stageKey);
      processingStage.current.delete(stageKey);

      // Deferred drain: if new updates accumulated for this stage while the
      // write was in flight, process them now. The recursive call will handle
      // its own flush-callback resolution when it completes.
      if (pendingUpdates.current.has(stageKey)) {
        processQueue(stageKey);
        return;
      }

      // All queues drained — resolve any waiting flush callbacks
      if (pendingUpdates.current.size === 0 && inFlightWrites.current.size === 0) {
        const cbs = flushCallbacks.current.splice(0);
        cbs.forEach(cb => cb.resolve());
      }
    });

    return writePromise;
  }, [applyDotPathUpdates, patchAssessmentStageLocalStorage]);

  /**
   * Queue a partial update to an assessment stage document using Firestore
   * dot-path notation. Updates are accumulated in a per-stage buffer and
   * flushed to Firestore after a 200ms debounce (same interval as the ADHD
   * PatientDataContext), batching rapid successive calls into a single write.
   *
   * Returns Promise.resolve() immediately for backward compatibility (same
   * contract as PatientDataContext's updatePatientFirestore). The screen
   * updates in the same turn. The Firestore write happens when the debounce
   * timer fires or when flushStageWrites is called.
   *
   * @param {string} stageKey - e.g. 'history', 'semiStructuredInterview'
   * @param {Object} dotPathUpdates - e.g. { 'data.criteria.A1.notes_childhood': [...] }
   */
  const updateAssessmentStage = useCallback((stageKey, dotPathUpdates) => {
    const patientId = loadedPatientIdRef.current;
    if (!patientId) return;
    if (!dotPathUpdates || Object.keys(dotPathUpdates).length === 0) return;
    if (!auth.currentUser) return;

    // Paint the click before the save. Adult ADHD does this with component
    // state (setSelections, then updatePatientFirestore). Modular stages are
    // read straight from this context, so the context itself has to move
    // first.
    setAssessmentStages((prev) => {
      const currentStage = prev[stageKey] || {};
      return {
        ...prev,
        [stageKey]: applyDotPathUpdates(currentStage, dotPathUpdates),
      };
    });

    // localStorage is NOT patched here. It is patched once per debounced
    // batch in processQueue (see the note there), so typing in a text field
    // does not re-serialise the whole patient cache on every keystroke.

    // [ASD debug] Trace every queued stage write (keys + the headline
    // progress/status fields that most often misbehave). See autismDebugLog.js.
    asdLog('ctx', 'queue write', {
      stageKey,
      keys: Object.keys(dotPathUpdates),
      progress: dotPathUpdates.progress,
      status: dotPathUpdates.status,
    });

    // Merge new dot-path updates into the stage's pending buffer. Later values
    // for the same dot-path key overwrite earlier ones (last-write-wins), which
    // is correct because each call sends the complete value for that field —
    // matching Firestore's server-side dot-path merge semantics.
    pendingUpdates.current.set(stageKey, {
      ...(pendingUpdates.current.get(stageKey) || {}),
      ...dotPathUpdates,
    });

    // Reset the debounce timer for this stage. Rapid successive calls
    // (e.g. Enter-Enter-Enter in StructuredNotesEditor) are batched into
    // a single Firestore write, reducing write volume and billing.
    clearTimeout(debounceTimers.current.get(stageKey));
    debounceTimers.current.set(
      stageKey,
      setTimeout(() => processQueue(stageKey), DEBOUNCE_MS),
    );

    return Promise.resolve();
  }, [processQueue, applyDotPathUpdates]);

  /**
   * Flush all pending writes for the given stage (or all stages).
   *
   * 1. Blurs the active element so any onBlur handler queues its write
   * 2. Clears debounce timers and kicks processQueue immediately for
   *    any stage that has accumulated updates
   * 3. Awaits the resulting Firestore write(s) via the inFlightWrites
   *    tracking and flushCallbacks mechanism
   *
   * Analogous to PatientDataContext.flushPendingUpdates — use before navigation,
   * patient switch, or AI generation that reads from Firestore.
   */
  const flushStageWrites = useCallback((stageKey) => {
    // [ASD debug] Trace flush requests (usually right before navigation or AI
    // generation, so seeing this confirms data was committed first).
    asdLog('ctx', 'flush requested', { stageKey: stageKey || 'ALL' });
    // Blur so any component's onBlur handler queues its pending write
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    // Immediately drain any debounce-queued updates for the specified stage(s).
    // This bypasses the 200ms timer and kicks processQueue synchronously so
    // the write promise is tracked in inFlightWrites before we check below.
    const stagesToFlush = stageKey
      ? [stageKey]
      : [...new Set([...debounceTimers.current.keys(), ...pendingUpdates.current.keys()])];

    for (const sk of stagesToFlush) {
      clearTimeout(debounceTimers.current.get(sk));
      debounceTimers.current.delete(sk);
      if (pendingUpdates.current.has(sk)) {
        processQueue(sk);
      }
    }

    // Collect in-flight promises for the requested scope
    const promises = [];
    if (stageKey) {
      const stageSet = inFlightWrites.current.get(stageKey);
      if (stageSet) promises.push(...stageSet);
    } else {
      for (const stageSet of inFlightWrites.current.values()) {
        promises.push(...stageSet);
      }
    }

    // Fast path: nothing pending, nothing in-flight
    if (promises.length === 0 && pendingUpdates.current.size === 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      flushCallbacks.current.push({ resolve });
      // Safety timeout: resolve after 5s even if writes are stuck, matching
      // PatientDataContext's default behaviour.
      setTimeout(() => {
        const idx = flushCallbacks.current.findIndex(cb => cb.resolve === resolve);
        if (idx !== -1) {
          flushCallbacks.current.splice(idx, 1);
          resolve();
        }
      }, 5000);
    });
  }, [processQueue]);

  /**
   * Internal: process the pending update queue for a single reportContent doc.
   * Identical flow to processQueue (for assessmentStages) but targets the
   * reportContent subcollection and updates reportContent state.
   */
  const processReportContentQueue = useCallback(async (stageKey) => {
    const rcKey = `rc_${stageKey}`;
    if (processingStage.current.has(rcKey)) return;

    const updates = pendingUpdates.current.get(rcKey);
    pendingUpdates.current.delete(rcKey);
    if (!updates || Object.keys(updates).length === 0) {
      if (pendingUpdates.current.size === 0 && inFlightWrites.current.size === 0) {
        const cbs = flushCallbacks.current.splice(0);
        cbs.forEach(cb => cb.resolve());
      }
      return;
    }

    const patientId = loadedPatientIdRef.current;
    if (!patientId) return;
    if (!auth.currentUser) return;

    processingStage.current.add(rcKey);

    const rcDocSegments = getReportSegments(patientId, stageKey);
    const rcDocRef = doc(db, ...rcDocSegments);
    const payload = stripUndefined({ ...updates, updatedAt: serverTimestamp() });
    inFlightBatches.current.set(rcKey, updates);

    const writePromise = (async () => {
      let lastError = null;
      for (let attempt = 1; attempt <= MAX_WRITE_RETRIES; attempt++) {
        try {
          if (!auth.currentUser) return;
          await updateDoc(rcDocRef, payload);

          setReportContent(prev => {
            const currentDoc = prev[stageKey] || {};
            const patched = applyDotPathUpdates(currentDoc, updates);
            patchReportContentLocalStorage(stageKey, patched);
            return { ...prev, [stageKey]: patched };
          });
          return;
        } catch (err) {
          lastError = err;
          const code = err?.code || '';
          if (['permission-denied', 'not-found', 'unauthenticated'].includes(code)) {
            break;
          }
          if (attempt < MAX_WRITE_RETRIES) {
            await new Promise(r => setTimeout(r, 200 * attempt));
          }
        }
      }

      const code = lastError?.code || 'unknown';
      console.error(
        `[ModularPatientDataContext] ReportContent write failed for ${stageKey}`,
        `code=${code}`,
        `paths=${Object.keys(updates).join(', ')}`,
        `docPath=${rcDocSegments.join('/')}`,
        lastError,
      );
      const devSuffix = process.env.NODE_ENV !== 'production' ? ` [${code}]` : '';
      toast.error(
        `Your latest changes may not have saved. Please check your connection and try again.${devSuffix}`,
        { toastId: WRITE_ERROR_TOAST_ID },
      );
    })();

    if (!inFlightWrites.current.has(rcKey)) {
      inFlightWrites.current.set(rcKey, new Set());
    }
    inFlightWrites.current.get(rcKey).add(writePromise);

    writePromise.finally(() => {
      const stageSet = inFlightWrites.current.get(rcKey);
      if (stageSet) {
        stageSet.delete(writePromise);
        if (stageSet.size === 0) inFlightWrites.current.delete(rcKey);
      }
      inFlightBatches.current.delete(rcKey);
      processingStage.current.delete(rcKey);

      if (pendingUpdates.current.has(rcKey)) {
        processReportContentQueue(stageKey);
        return;
      }

      if (pendingUpdates.current.size === 0 && inFlightWrites.current.size === 0) {
        const cbs = flushCallbacks.current.splice(0);
        cbs.forEach(cb => cb.resolve());
      }
    });

    return writePromise;
  }, [applyDotPathUpdates, patchReportContentLocalStorage]);

  /**
   * Queue a partial update to a reportContent document using Firestore
   * dot-path notation. Same debounce/mutex/flush pattern as updateAssessmentStage
   * but targets the reportContent subcollection. Uses `rc_` prefixed keys in the
   * shared pending-updates map to avoid collision with assessmentStages entries.
   *
   * @param {string} stageKey - e.g. 'history', 'semiStructuredInterview'
   * @param {Object} dotPathUpdates - e.g. { 'approvedParagraphs.asd_ci_COM_referral_information': {...} }
   */
  const updateReportContent = useCallback((stageKey, dotPathUpdates) => {
    const patientId = loadedPatientIdRef.current;
    if (!patientId) return;
    if (!dotPathUpdates || Object.keys(dotPathUpdates).length === 0) return;
    if (!auth.currentUser) return;

    const rcKey = `rc_${stageKey}`;
    pendingUpdates.current.set(rcKey, {
      ...(pendingUpdates.current.get(rcKey) || {}),
      ...dotPathUpdates,
    });

    clearTimeout(debounceTimers.current.get(rcKey));
    debounceTimers.current.set(
      rcKey,
      setTimeout(() => processReportContentQueue(stageKey), DEBOUNCE_MS),
    );

    return Promise.resolve();
  }, [processReportContentQueue]);

  /**
   * Re-read a single reportContent document from Firestore and update context
   * state + localStorage. Use after Cloud Function generation completes so
   * the frontend picks up newly generated paragraphs without a full refetch.
   */
  const refreshReportContent = useCallback(async (patientId, stageKey) => {
    try {
      const rcDocSegments = getReportSegments(patientId, stageKey);
      const rcDocRef = doc(db, ...rcDocSegments);
      const rcDoc = await getDoc(rcDocRef);
      if (rcDoc.exists()) {
        const rcData = rcDoc.data();
        setReportContent(prev => {
          const updated = { ...prev, [stageKey]: rcData };
          patchReportContentLocalStorage(stageKey, rcData);
          return updated;
        });
      }
    } catch (err) {
      console.error(`[ModularPatientDataContext] Error refreshing reportContent ${stageKey}:`, err);
    }
  }, [patchReportContentLocalStorage]);

  /**
   * Re-read a single assessmentStage document from Firestore and update
   * context state + localStorage. Mirrors refreshFormInstance.
   *
   * Use after operations where server-side state may have changed outside
   * the current session (e.g. access sync, admin overrides, Cloud Function
   * writes).
   */
  const refreshAssessmentStage = useCallback(async (patientId, stageKey) => {
    try {
      const stageDocSegments = getStageSegments(patientId, stageKey);
      const stageDocRef = doc(db, ...stageDocSegments);
      const stageDoc = await getDoc(stageDocRef);
      if (stageDoc.exists()) {
        const stageData = stageDoc.data();
        setAssessmentStages(prev => {
          const updated = { ...prev, [stageKey]: stageData };
          patchAssessmentStageLocalStorage(stageKey, stageData);
          return updated;
        });
      }
    } catch (err) {
      console.error(`[ModularPatientDataContext] Error refreshing assessmentStage ${stageKey}:`, err);
    }
  }, [patchAssessmentStageLocalStorage]);

  // ─── Route-change flush ───────────────────────────────────────────
  // When the URL changes, flush all debounce-queued and in-flight writes
  // before the next route renders. flushStageWrites already blurs the
  // active element internally, so StructuredNotesEditor's onBlur commits
  // any uncommitted draft note before the queue is drained.
  const location = useLocation();

  useLayoutEffect(() => {
    return () => {
      // Fire-and-forget: does not block navigation, but ensures the
      // debounce timer is cancelled and processQueue runs immediately.
      flushStageWrites();
    };
  }, [location.pathname, flushStageWrites]);

  /**
   * Fetch patient data from Firestore: header doc + both subcollections in parallel.
   * Same access-control flow as PatientDataContext (clinician doc check + retry-once).
   */
  const fetchPatientData = useCallback(async (patientId) => {
    try {
      setError(null);

      const clinicianData = await getUserClinicianDoc();
      if (!clinicianData) {
        throw new Error('Not authorized to access patient data');
      }

      const patientDocRef = doc(db, 'patients', patientId);
      const patientDoc = await getDoc(patientDocRef);

      if (!patientDoc.exists()) {
        clearPatientData();
        navigate('/dashboard', { state: { error: `Patient ${patientId} not found` } });
        throw new Error(`Patient ${patientId} not found`);
      }

      const patientData = patientDoc.data();

      // Access check with retry-once for stale clinician cache
      let hasAccess = checkClinicianAccess(clinicianData, patientId, patientData);
      if (!hasAccess) {
        const freshClinicianData = await getUserClinicianDoc({ forceRefresh: true });
        if (freshClinicianData) {
          hasAccess = checkClinicianAccess(freshClinicianData, patientId, patientData);
        }
      }

      if (!hasAccess) {
        clearPatientData();
        navigate('/dashboard', { state: { error: `You don't have access to patient ${patientId}` } });
        throw new Error(`Not authorized to access patient ${patientId}`);
      }

      // Fetch subcollections in parallel with the header already in hand.
      // Clinician list queries must include where() filters that match the
      // denormalised access fields in firestore.rules; Org Admin/Support use a
      // path-based rule and can query the whole subcollection.
      const isOrgWideAccess = hasOrgWideAutismSubcollectionAccess(clinicianData.userType);
      const subcollectionAccess = {
        organisation: patientData.organisation,
        clinicianUid: auth.currentUser?.uid,
        isOrgWideAccess,
      };
      // Resolve the pathway subcollection from the patient's assessmentModule
      // so this context works for any modular module (Adult Autism, CYP ADHD, etc.).
      const patientModule = patientData.assessmentModule || 'adultAutism';
      const moduleCol = getModuleCollection(patientModule) || 'adult_autism';
      const moduleDocId = getModuleAssessmentDocId(patientModule) || 'assessment_001';
      loadedModuleRef.current = patientModule;

      const assessmentBase = ['patients', patientId, moduleCol, moduleDocId];

      // CYP ADHD stores questionnaires on informant trees only. Skip the
      // patient-side formInstances list so we do not depend on an empty
      // collection query succeeding (and so we do not pay for a no-op read).
      const shouldLoadPatientForms = moduleHasPatientFormInstances(patientModule);

      const [formInstancesSnap, assessmentStagesSnap, reportContentSnap] = await Promise.all([
        shouldLoadPatientForms
          ? getDocs(buildAutismSubcollectionQuery(db, [...assessmentBase, 'formInstances'], subcollectionAccess))
          : Promise.resolve(null),
        getDocs(buildAutismSubcollectionQuery(db, [...assessmentBase, 'assessmentStages'], subcollectionAccess)),
        getDocs(buildAutismSubcollectionQuery(db, [...assessmentBase, 'reportContent'], subcollectionAccess)),
      ]);

      const formInstancesData = {};
      if (formInstancesSnap) {
        formInstancesSnap.forEach(docSnap => {
          formInstancesData[docSnap.id] = docSnap.data();
        });
      }

      const assessmentStagesData = {};
      assessmentStagesSnap.forEach(docSnap => {
        assessmentStagesData[docSnap.id] = docSnap.data();
      });

      const reportContentData = {};
      reportContentSnap.forEach(docSnap => {
        reportContentData[docSnap.id] = docSnap.data();
      });

      // Fetch informant history stages and form instances in parallel.
      // Informant UIDs are stored on the patient header in the informants map.
      let informantHistoryData = {};
      let informantFormInstancesData = {};
      const informantMap = patientData?.informants || {};
      const informantUids = Object.keys(informantMap);

      if (informantUids.length > 0) {
        const informantFetchPromises = informantUids.map(async (informantUid) => {
          try {
            const assessmentBase = [
              'informants', informantUid, 'patients', patientId,
              moduleCol, moduleDocId,
            ];
            // CYP ADHD has no informant history stage — forms prefill CI
            // directly. Skipping the getDoc avoids a permission-denied on a
            // path that has no assessmentStages matcher under cyp_adhd.
            const formInstancesQuery = buildAutismSubcollectionQuery(
              db,
              [...assessmentBase, 'formInstances'],
              subcollectionAccess,
            );

            const [historySnap, formInstancesSnap] = await Promise.all([
              moduleHasInformantHistoryStage(patientModule)
                ? getDoc(doc(db, ...assessmentBase, 'assessmentStages', 'history'))
                : Promise.resolve(null),
              getDocs(formInstancesQuery),
            ]);

            const formInstances = {};
            formInstancesSnap.forEach((docSnap) => {
              formInstances[docSnap.id] = docSnap.data();
            });

            return {
              uid: informantUid,
              history: historySnap?.exists() ? historySnap.data() : null,
              formInstances,
            };
          } catch {
            // Non-critical: informant may not have assessment data yet
          }
          return null;
        });

        const results = await Promise.all(informantFetchPromises);
        results.forEach((result) => {
          if (!result) return;
          if (result.history) informantHistoryData[result.uid] = result.history;
          // Always key by uid so lookups stay aligned with header.informants
          informantFormInstancesData[result.uid] = result.formInstances || {};
        });
      }

      // Lay outstanding writes for THIS patient back over the server snapshot.
      //
      // The snapshot above is what Firestore held when the reads were answered.
      // Any batch that was still travelling (inFlightBatches) or still waiting
      // for its debounce timer (pendingUpdates) at that moment is not in it.
      // Replacing state with the bare snapshot would paint the pre-click value
      // for a round-trip until the write's own acknowledgement re-applied it,
      // and the cache written below would carry the same gap. Both cases are
      // real in the cold-load window (page interactive from cache, fetch still
      // out) and after a mid-page fetch such as the review-document mount.
      //
      // Order matters: server, then in-flight (older), then pending (newest),
      // the same last-write-wins the queue itself uses. Keys of the form
      // "rc_<stage>" belong to reportContent, everything else to
      // assessmentStages (the queue shares one map for both).
      //
      // Guarded to the same patient: when this fetch is a patient switch, any
      // outstanding batches belong to the previous patient and must not be
      // painted onto the new one.
      if (loadedPatientIdRef.current === patientId) {
        const outstandingKeys = new Set([
          ...inFlightBatches.current.keys(),
          ...pendingUpdates.current.keys(),
        ]);
        for (const key of outstandingKeys) {
          const overlay = (fetched) => {
            let result = fetched || {};
            const inFlight = inFlightBatches.current.get(key);
            if (inFlight) result = applyDotPathUpdates(result, inFlight);
            const pending = pendingUpdates.current.get(key);
            if (pending) result = applyDotPathUpdates(result, pending);
            return result;
          };
          if (key.startsWith('rc_')) {
            const rcStageKey = key.slice('rc_'.length);
            reportContentData[rcStageKey] = overlay(reportContentData[rcStageKey]);
          } else {
            assessmentStagesData[key] = overlay(assessmentStagesData[key]);
          }
        }
      }

      // Update state — header first, then loadedPatientId (same ordering guarantee
      // as PatientDataContext to prevent intermediate renders showing wrong data)
      setHeader(patientData);
      setFormInstances(formInstancesData);
      setAssessmentStages(assessmentStagesData);
      setReportContent(reportContentData);
      setInformantHistoryStages(informantHistoryData);
      setInformantFormInstances(informantFormInstancesData);
      loadedPatientIdRef.current = patientId;
      fetchConfirmedPatientIdRef.current = patientId;
      setLoadedPatientId(patientId);

      // Persist to localStorage for instant paint on same-patient reload
      const cachePayload = {
        header: patientData,
        formInstances: formInstancesData,
        assessmentStages: assessmentStagesData,
        reportContent: reportContentData,
        informantHistoryStages: informantHistoryData,
        informantFormInstances: informantFormInstancesData,
      };
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(cachePayload));
      localStorage.setItem(LOADED_PATIENT_ID_KEY, patientId);

    } catch (err) {
      console.error('[ModularPatientDataContext] Error fetching patient data:', err);
      // Decided on the fetch-confirmed id, not the cache-seeded write target:
      // a cold load that fails must clear and surface the error exactly as it
      // always has (and as PatientDataContext does), even though the write
      // refs were seeded from the cache for the sake of early clicks.
      if (fetchConfirmedPatientIdRef.current === patientId) {
        // Same patient re-fetch failed transiently — preserve existing data
        console.warn('[ModularPatientDataContext] Transient fetch error, preserving data');
      } else {
        setError(err.message);
        clearPatientData();
      }
    }
  }, [getUserClinicianDoc, navigate, clearPatientData, applyDotPathUpdates]);

  /**
   * Re-read a single formInstance document after a write (e.g. future proforma save).
   * Avoids re-fetching the entire patient + all subcollections for a single-doc change.
   */
  const refreshFormInstance = useCallback(async (patientId, formKey) => {
    try {
      const mod = loadedModuleRef.current || 'adultAutism';
      const col = getModuleCollection(mod) || 'adult_autism';
      const docId = getModuleAssessmentDocId(mod) || 'assessment_001';
      const fiRef = doc(db, 'patients', patientId, col, docId, 'formInstances', formKey);
      const fiDoc = await getDoc(fiRef);
      if (fiDoc.exists()) {
        setFormInstances(prev => {
          const updated = { ...prev, [formKey]: fiDoc.data() };
          // Update localStorage cache
          try {
            const stored = localStorage.getItem(LOCALSTORAGE_KEY);
            if (stored) {
              const parsed = JSON.parse(stored);
              parsed.formInstances = updated;
              localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(parsed));
            }
          } catch { /* non-critical */ }
          return updated;
        });
      }
    } catch (err) {
      console.error(`[ModularPatientDataContext] Error refreshing formInstance ${formKey}:`, err);
    }
  }, []);

  return (
    <ModularPatientDataContext.Provider value={{
      header,
      formInstances,
      assessmentStages,
      reportContent,
      informantHistoryStages,
      informantFormInstances,
      loadedPatientId,
      fetchPatientData,
      refreshFormInstance,
      refreshAssessmentStage,
      refreshReportContent,
      updateAssessmentStage,
      updateReportContent,
      flushStageWrites,
      clearPatientData,
      error,
    }}>
      {children}
    </ModularPatientDataContext.Provider>
  );
};
